import React, { useState, useEffect , useCallback , useMemo } from 'react';


import { useDispatch } from 'react-redux';
import { Box, Grid } from '@mui/material';

import { ViewMoreLink } from 'components/common/ViewMoreLink';
import StudyOpportunityCard from 'components/StudyOpportunityCard';
import { EmptyContainer } from 'shared-components/EmptyContainer';
import {
  StudyOpportunitiesWrapper,
  StudyOpportunitiesHeader,
  Title,
  StudyOpportunitiesContent,
} from './styles';
import { ExploreMoreCard } from 'components/StudyOpportunityCard/ExploreMoreCard';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';
import { useGetProfileParticipantTasksQuery, useGetProfileParticipantTaskQuery } from 'store/api/pkd/endpoints/participantTask';
import { openSnackbar } from 'store/application';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import {
  getAttributesPayload,
  getPersonAttribute,
} from 'pages/MedicalInformation/utils';
import { TaskTitles } from 'utils/enum';
import {
  getUsersOpportunities,
  studyOpportunitiesItems,
} from 'pages/CTEReport/utils';
import { BasicSkeleton } from 'components/SkeletonLoaders';
import EmptyCard from 'components/StudyOpportunityCard/EmptyCard';

const StudyOpportunities = () => {
  const dispatch = useDispatch();
  const { isPersonLoading, isPersonAttributesFetching, person, personId, personAccountProfileId } =
    useGetPersonAndAttributes();

  const { participantTask, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      { skip             : !personAccountProfileId ,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTask          : data?.find(item => item.taskTitle === TaskTitles.coreQuestionnaire),
          isParticipantTaskLoading : isFetching,
        }),
      }
    );
   
  const { data:coreQuestionnaire, isLoading:isCoreQuestionnaireLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: participantTask?.id },
      { skip: !participantTask }
    );

  const [upsertPersonAttribute] = useUpsertPersonAttributeMutation();

  const allOpportunities = getUsersOpportunities(coreQuestionnaire);
  const opportunities = allOpportunities.slice(0, 3);
  const studyOpportunitiesEntries = getPersonAttribute(
    person?.attributes,
    Object.fromEntries(
      Object.keys(studyOpportunitiesItems).map((item) => [item, ''])
    )
  );

  // States
  const [activeStudyOpportunity, setActiveStudyOpportunity] = useState('');

  // used for setting data
  const transformedData = useMemo(() => {
    const data = {};
    [].forEach((study) => {
      data[study.key] = study.isEnrolled;
    });
    return data;
  }, []);

  const handleEnrollToStudyOpportunity = async (payload) => {
    const attributes = getPersonAttribute(
      person?.attributes,
      studyOpportunitiesEntries
    );
    const body = getAttributesPayload(studyOpportunitiesEntries, attributes);
    if (body.length > 0) {
      const { error: upsertPersonError } = await upsertPersonAttribute({
        body,
        personId,
      });
      if (upsertPersonError) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not enroll you to this study opportunity. Please try again',
          })
        );
      }
    }
    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Study opportunity enrollment successfully saved',
      })
    );
  };

  const onClickEnroll = useCallback(
    async (id) => {
      setActiveStudyOpportunity(id);
      studyOpportunitiesEntries[id] = 'true';
      // make api call
      await handleEnrollToStudyOpportunity();
    },
    [transformedData]
  );

  useEffect(() => {
    if (!isPersonLoading && !isPersonAttributesFetching) {
      setActiveStudyOpportunity('');
    }
  }, [isPersonLoading, isPersonAttributesFetching]);

  const isLoadingOpportunity = isPersonLoading || isCoreQuestionnaireLoading || isParticipantTaskLoading;
  return (
    <StudyOpportunitiesWrapper>
      <StudyOpportunitiesHeader
        sx={{
          display       : 'flex',
          flexDirection : { xs: 'column', md: 'row' },
        }}>
        <Title>Study opportunities</Title>
        {allOpportunities.length > 0 && (
          <ViewMoreLink
            to={'/homepage/clinical-study-eligibility-report'}>
            More details
          </ViewMoreLink>
        )}
      </StudyOpportunitiesHeader>
      {isLoadingOpportunity
        ? (
          <BasicSkeleton
            height='400px' width='400px'
          />
        )
        : (
          <StudyOpportunitiesContent>
            {opportunities.length > 0
              ? (
                <Grid
                  container spacing={1}>
                  {opportunities.map((item) => (
                    <Grid
                      key={item.id}
                      item
                      md={6}
                      lg={3}
                      sx={{ flex: 1, display: 'flex' }}>
                      <StudyOpportunityCard
                        sx={{ flex: 1 }}
                        item={item}
                        studyOpportunitiesEntries={studyOpportunitiesEntries}
                        onEnroll={() => onClickEnroll(item.key)}
                        enrollButtonDisabled={
                          activeStudyOpportunity === item.key ||
                      (isPersonAttributesFetching &&
                        activeStudyOpportunity === '')
                        }
                        isLoading={isPersonLoading}
                        enrollButtonLoading={
                          activeStudyOpportunity === item.key ||
                      (isPersonAttributesFetching &&
                        activeStudyOpportunity === '')
                        }
                      />
                    </Grid>
                  ))}
                  <Grid
                    item md={6} lg={3} sx={{ flex: 1, display: 'flex' }}>
                    <ExploreMoreCard
                      sx={{ flex: 1 }}
                    />
                  </Grid>
                </Grid>
              )
              : (
                <Box
                  display='flex' gap='30px'>
                  <EmptyCard
                    sx={{ flex: 1 }} taskId={coreQuestionnaire?.participantTaskId}
                  />
                  <ExploreMoreCard
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
          </StudyOpportunitiesContent>
        )}
    </StudyOpportunitiesWrapper>
  );
};

export default StudyOpportunities;
